import React, { useRef, useState } from "react";
import { Button, Text, Loader, ActionIcon } from "@mantine/core";
import { IconPaperclip, IconX } from "@tabler/icons-react";
import { NotificationExtension } from "../../extension/NotificationExtension";
import { uploadFile, UploadFileResult } from "../../extension/Extension";

export interface CommentAttachmentSectionProps {
  onAttachmentsChange: (urls: string) => void;
}

export const CommentAttachmentSection = ({ onAttachmentsChange }: CommentAttachmentSectionProps) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadFileResult[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (files: File[] | null) => {
    if (!files || files.length === 0) return;
    
    setIsUploading(true);
    try {
      const result = await uploadFile(Array.from(files));
      if (result && result.length > 0) {
        const newFiles = [...uploadedFiles, ...result];
        setUploadedFiles(newFiles);
        // Update parent with comma-separated URLs
        const urlsString = newFiles.map(f => f.url).join(",");
        onAttachmentsChange(urlsString);
        NotificationExtension.Success("Tải file thành công!");
      } else {
        NotificationExtension.Fails("Tải file thất bại!");
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      NotificationExtension.Fails("Lỗi khi tải file!");
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);
    const urlsString = newFiles.map(f => f.url).join(",");
    onAttachmentsChange(urlsString);
  };

  const getFileName = (file: UploadFileResult) => {
    return file.pathNameBefore || file.objectName || "File";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div
      style={{
        marginTop: "16px",
        padding: "16px",
        backgroundColor: "#fff8e1",
        borderRadius: "12px",
        border: "1px solid #ffe082",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <Text size="sm" fw={600} c="dark.7">
          📎 File đính kèm:
        </Text>
        <input
          type="file"
          ref={fileInputRef}
          multiple
          style={{ display: "none" }}
          onChange={(e) => handleFileSelect(e.target.files ? Array.from(e.target.files) : null)}
        />
        <Button
          variant="light"
          color="orange"
          size="xs"
          leftSection={isUploading ? <Loader size={14} /> : <IconPaperclip size={14} />}
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading ? "Đang tải..." : "Thêm đính kèm"}
        </Button>
      </div>

      {uploadedFiles.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {uploadedFiles.map((file, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "10px 12px",
                backgroundColor: "white",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
                <IconPaperclip size={18} color="#f59f00" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    size="sm"
                    fw={500}
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getFileName(file)}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {formatFileSize(file.size)}
                  </Text>
                </div>
              </div>
              <ActionIcon
                variant="light"
                color="red"
                size="sm"
                onClick={() => handleRemoveFile(index)}
              >
                <IconX size={14} />
              </ActionIcon>
            </div>
          ))}
        </div>
      ) : (
        <Text size="sm" c="dimmed" ta="center" py="md">
          Chưa có file đính kèm
        </Text>
      )}
    </div>
  );
};
